/**
 * pack-builder-collection-products.js
 *
 * Responsabilités :
 *   • Écouter les clics sur les boutons "Ajouter à la sélection" ([data-pack-add]).
 *   • Écouter les changements de quantité sur les inputs ([data-pack-qty-wrap]).
 *   • Mettre à jour l'UI des cartes produits (bouton ↔ sélecteur de quantité)
 *     au chargement (hydratation depuis localStorage) et quand
 *     `pack-builder:selection-changed` est émis.
 *
 * Communication avec PackBuilder via appels directs sur `closest('pack-builder')`.
 */

/** Guard pour éviter une boucle change ↔ setVariantQty lors des màj programmatiques. */
let isApplyingUi = false;

function syncPackProductCardsFromSelection() {
  isApplyingUi = true;

  try {
    for (const root of document.querySelectorAll('[data-pack-selection-root]')) {
      if (!(root instanceof HTMLElement)) continue;

      const pb = root.closest('pack-builder');
      if (!pb) continue;

      const vid = root.dataset.variantId ?? '';
      if (!vid) continue;

      const sel = pb.getSelection();
      const line = sel.lines.find((l) => String(l.variantId) === String(vid));
      const qty = line?.quantity ?? 0;

      const addBtn = root.querySelector('[data-pack-add]');
      const qtyWrap = root.querySelector('[data-pack-qty-wrap]');
      const qtySelector = qtyWrap?.querySelector('quantity-selector');

      const item = root.closest('.pack-builder-collection-products__item');
      if (item instanceof HTMLElement) {
        item.classList.toggle('pack-builder-collection-products__item--selected', qty > 0);
      }

      if (qty > 0) {
        if (addBtn instanceof HTMLElement) addBtn.hidden = true;
        if (qtyWrap instanceof HTMLElement) qtyWrap.hidden = false;

        if (qtySelector && typeof qtySelector.setQuantity === 'function') {
          qtySelector.setQuantity(qty);
        } else {
          const input = qtyWrap?.querySelector('input[type="number"]');
          if (input instanceof HTMLInputElement) input.value = String(qty);
        }
      } else {
        if (addBtn instanceof HTMLElement) addBtn.hidden = false;
        if (qtyWrap instanceof HTMLElement) qtyWrap.hidden = true;

        if (qtySelector && typeof qtySelector.setQuantity === 'function') {
          qtySelector.setQuantity(1);
        } else {
          const input = qtyWrap?.querySelector('input[type="number"]');
          if (input instanceof HTMLInputElement) input.value = '1';
        }
      }
    }
  } finally {
    isApplyingUi = false;
  }
}

// ── Bouton "Ajouter à la sélection" ──────────────────────────────────────────

document.addEventListener('click', (event) => {
  const btn = event.target?.closest('[data-pack-add]');
  if (!(btn instanceof HTMLButtonElement) || btn.disabled) return;

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const pb = btn.closest('pack-builder');
  if (!pb) return;

  pb.addVariant(variantId);
});

// ── Sélecteur de quantité sur les cartes ─────────────────────────────────────

document.addEventListener('change', (event) => {
  if (isApplyingUi) return;

  const target = event.target;
  if (!(target instanceof HTMLInputElement) || target.type !== 'number') return;

  const wrap = target.closest('[data-pack-qty-wrap]');
  if (!wrap) return;

  const root = wrap.closest('[data-pack-selection-root]');
  if (!(root instanceof HTMLElement)) return;

  const vid = root.dataset.variantId;
  if (!vid) return;

  const qty = Math.max(0, parseInt(target.value, 10) || 0);
  const pb = root.closest('pack-builder');
  if (!pb) return;

  pb.setVariantQty(vid, qty);
});

// ── Mise à jour UI des cartes quand la sélection change ──────────────────────

window.addEventListener('pack-builder:selection-changed', syncPackProductCardsFromSelection);

/** Au chargement, localStorage est déjà rempli mais aucun événement n’est émis — on hydrate l’UI. */
function scheduleInitialPackProductCardsSync() {
  syncPackProductCardsFromSelection();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleInitialPackProductCardsSync);
} else {
  queueMicrotask(scheduleInitialPackProductCardsSync);
}
